import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  activityLogService,
  ActivityLog,
  ActivityLogListParams
} from '@/services/activityLog/activityLogService';

export interface ActivityLogState {
  activityLogs: ActivityLog[];
  activityLogsLoading: boolean;
  error: string | null;
  pagination: {
    totalDocs: number;
    limit: number;
    totalPages: number;
    page: number;
    pagingCounter: number;
    hasPrevPage: boolean;
    hasNextPage: boolean;
    prevPage: number | null;
    nextPage: number | null;
  } | null;
}

const initialState: ActivityLogState = {
  activityLogs: [],
  activityLogsLoading: false,
  error: null,
  pagination: null,
};

export const fetchActivityLogs = createAsyncThunk(
  'activityLog/fetchActivityLogs',
  async (params: ActivityLogListParams, { rejectWithValue }) => {
    try {
      const response = await activityLogService.getActivityLogs(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch activity logs');
    }
  }
);

export const activityLogSlice = createSlice({
  name: 'activityLog',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearActivityLogs: (state) => {
      state.activityLogs = [];
      state.pagination = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Activity Logs
      .addCase(fetchActivityLogs.pending, (state) => {
        state.activityLogsLoading = true;
        state.error = null;
      })
      .addCase(fetchActivityLogs.fulfilled, (state, action) => {
        state.activityLogsLoading = false;
        state.activityLogs = action.payload.data.docs;
        state.pagination = {
          totalDocs: action.payload.data.totalDocs,
          limit: action.payload.data.limit,
          totalPages: action.payload.data.totalPages,
          page: action.payload.data.page,
          pagingCounter: action.payload.data.pagingCounter,
          hasPrevPage: action.payload.data.hasPrevPage,
          hasNextPage: action.payload.data.hasNextPage,
          prevPage: action.payload.data.prevPage,
          nextPage: action.payload.data.nextPage,
        };
        state.error = null;
      })
      .addCase(fetchActivityLogs.rejected, (state, action) => {
        state.activityLogsLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearActivityLogs } = activityLogSlice.actions;
export default activityLogSlice.reducer;
